import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Leaf, ArrowLeft, Coins, Timer, Play, Lightbulb } from '@phosphor-icons/react';

const ANIMALS = [
    { name: "KOALA", hint: "Sleepy eucalyptus eater", emoji: "🐨" },
    { name: "KANGAROO", hint: "Hops with a joey in its pouch", emoji: "🦘" },
    { name: "PLATYPUS", hint: "Duck-billed egg-laying mammal", emoji: "🦆" },
    { name: "WOMBAT", hint: "Cube-shaped poo maker", emoji: "🐻" },
    { name: "ECHIDNA", hint: "Spiny anteater", emoji: "🦔" },
    { name: "KOOKABURRA", hint: "Laughing bird", emoji: "🐦" },
    { name: "QUOKKA", hint: "World's happiest animal", emoji: "😊" },
    { name: "COCKATOO", hint: "Loud crested parrot", emoji: "🦜" },
    { name: "DINGO", hint: "Australia's wild dog", emoji: "🐕" },
    { name: "BILBY", hint: "Easter bunny of Australia", emoji: "🐰" },
    { name: "DUGONG", hint: "Gentle sea cow", emoji: "🐋" },
    { name: "CASSOWARY", hint: "Dangerous rainforest bird", emoji: "🐦‍⬛" },
];

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
}

function scramble(word) {
    let scrambled = word;
    let attempts = 0;
    while (scrambled === word && attempts < 20) { scrambled = shuffle(word.split('')).join(''); attempts++; }
    return scrambled;
}

export default function WordScrambleGame() {
    const { api } = useAuth();
    const [started, setStarted] = useState(false);
    const [words, setWords] = useState([]);
    const [current, setCurrent] = useState(0);
    const [guess, setGuess] = useState('');
    const [score, setScore] = useState(0);
    const [hintsUsed, setHintsUsed] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [time, setTime] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [result, setResult] = useState(null);
    const timerRef = useRef(null);
    const inputRef = useRef(null);

    const startGame = () => {
        setWords(shuffle(ANIMALS).slice(0, 8));
        setCurrent(0); setGuess(''); setScore(0); setHintsUsed(0);
        setShowHint(false); setFeedback(null); setTime(0);
        setGameOver(false); setResult(null); setStarted(true);
        timerRef.current = setInterval(() => setTime(t => t + 1), 1000);
    };

    useEffect(() => { return () => { if (timerRef.current) clearInterval(timerRef.current); }; }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const correct = guess.toUpperCase().trim() === words[current].name;
        if (correct) {
            const points = showHint ? 5 : 10;
            setScore(s => s + points);
            setFeedback('correct');
        } else {
            setFeedback('wrong');
        }
        setTimeout(() => {
            if (current + 1 >= words.length) {
                clearInterval(timerRef.current);
                setGameOver(true);
                completeGame();
            } else {
                setCurrent(c => c + 1);
                setGuess(''); setShowHint(false); setFeedback(null);
                inputRef.current?.focus();
            }
        }, 1200);
    };

    const handleSkip = () => {
        setFeedback('skipped');
        setTimeout(() => {
            if (current + 1 >= words.length) {
                clearInterval(timerRef.current); setGameOver(true); completeGame();
            } else {
                setCurrent(c => c + 1); setGuess(''); setShowHint(false); setFeedback(null);
            }
        }, 800);
    };

    const completeGame = async () => {
        try {
            const res = await api().post('/games/complete', {
                game_id: 'word_scramble', score: Math.round((score / 80) * 100), time_seconds: time, difficulty: 'medium'
            });
            setResult(res.data);
        } catch (err) { console.error('Failed to submit'); }
    };

    const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    if (!started) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-20">
                            <Link to="/games" className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-[#38bdf8] border-4 border-[#1e293b] flex items-center justify-center">
                                    <Leaf weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-[#1e293b]">Word Scramble</span>
                            </Link>
                            <Link to="/games" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5">
                                <ArrowLeft weight="bold" size={16} /> Games
                            </Link>
                        </div>
                    </div>
                </nav>
                <div className="max-w-2xl mx-auto px-4 pt-28 pb-24 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-6xl mb-4">🔤</div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b] mb-2">Word Scramble</h1>
                        <p className="text-[#475569] mb-8">Unscramble 8 Australian animal names. Use hints if you're stuck!</p>
                        <motion.button onClick={startGame} whileHover={{ y: -4, x: -4 }} whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn bg-[#38bdf8] text-white text-lg flex items-center gap-2 mx-auto" data-testid="start-game">
                            <Play weight="bold" size={22} /> Start
                        </motion.button>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (gameOver) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="brutalist-card p-8 max-w-md w-full text-center bg-white" data-testid="game-complete">
                    <div className="text-6xl mb-4">🎉</div>
                    <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Great Work!</h2>
                    <p className="text-[#475569] mb-6">Score: {score}/80</p>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        <div className="p-3 bg-[#f8fafc] rounded-xl">
                            <Timer weight="duotone" size={20} className="text-[#a855f7] mx-auto" />
                            <p className="font-fredoka text-xl font-bold">{formatTime(time)}</p><p className="text-xs text-[#94a3b8]">Time</p>
                        </div>
                        <div className="p-3 bg-[#fef3c7] rounded-xl">
                            <Coins weight="duotone" size={20} className="text-[#f59e0b] mx-auto" />
                            <p className="font-fredoka text-xl font-bold">{result ? `+${result.tokens_earned}` : '...'}</p><p className="text-xs text-[#94a3b8]">Tokens</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={startGame} className="brutalist-btn flex-1 bg-[#38bdf8] text-white"><Play weight="bold" size={18} /> Again</button>
                        <Link to="/games" className="brutalist-btn flex-1 bg-white text-[#1e293b] text-center">Games Hub</Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    const w = words[current];
    const scrambled = scramble(w.name);

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center p-4">
            <div className="max-w-lg w-full">
                <div className="flex items-center justify-between mb-4">
                    <Link to="/games" className="text-sm text-[#475569] font-semibold flex items-center gap-1"><ArrowLeft size={16} /> Games</Link>
                    <div className="flex gap-4 text-sm font-semibold">
                        <span>{current + 1}/{words.length}</span>
                        <span className="text-[#f59e0b]">Score: {score}</span>
                        <span className="text-[#a855f7]">{formatTime(time)}</span>
                    </div>
                </div>

                <motion.div key={current} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="brutalist-card p-6 bg-white text-center">
                    <div className="text-5xl mb-2">{w.emoji}</div>
                    <div className="flex justify-center gap-2 mb-6 flex-wrap">
                        {scrambled.split('').map((letter, i) => (
                            <span key={i} className="w-10 h-12 bg-[#f59e0b]/10 border-2 border-[#1e293b] rounded-lg flex items-center justify-center font-mono font-bold text-xl text-[#1e293b]">
                                {letter}
                            </span>
                        ))}
                    </div>

                    {showHint && <p className="text-sm text-[#475569] mb-4 italic">Hint: {w.hint}</p>}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <input ref={inputRef} type="text" value={guess} onChange={(e) => setGuess(e.target.value)}
                            className={`brutalist-input w-full text-center text-xl font-mono uppercase tracking-widest ${feedback === 'correct' ? 'border-[#22c55e] bg-[#22c55e]/10' : feedback === 'wrong' ? 'border-[#ef4444] bg-[#ef4444]/10' : ''}`}
                            placeholder="Type your answer..." autoFocus disabled={!!feedback} data-testid="answer-input" />
                        {feedback && (
                            <p className={`font-bold ${feedback === 'correct' ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
                                {feedback === 'correct' ? 'Correct!' : feedback === 'wrong' ? `It was ${w.name}` : `Skipped — ${w.name}`}
                            </p>
                        )}
                        {!feedback && (
                            <div className="flex gap-3">
                                <button type="button" onClick={() => { setShowHint(true); setHintsUsed(h => h + 1); }}
                                    disabled={showHint} className="brutalist-btn flex-1 bg-white text-[#1e293b] flex items-center justify-center gap-1 disabled:opacity-50">
                                    <Lightbulb weight="bold" size={16} /> Hint
                                </button>
                                <button type="submit" className="brutalist-btn flex-1 bg-[#22c55e] text-white" data-testid="submit-answer">Check</button>
                                <button type="button" onClick={handleSkip} className="brutalist-btn flex-1 bg-[#94a3b8] text-white">Skip</button>
                            </div>
                        )}
                    </form>
                </motion.div>
            </div>
        </div>
    );
}
